import { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../auth.css';
import { Release, fetchReleases } from '../api/site';
import { shotOf } from '../shared/siteContent';
import { useSiteContent } from './site/useSiteContent';

/**
 * The frames every signed-out page draws itself in (R13).
 *
 * `AuthSplit` is screens 04–05: the form on the canvas, and beside it an
 * aside — what is new (sign in) or the plan being started (sign up) — over a
 * product shot. Two columns of at least 420px each; one column below 840px;
 * the form alone on a phone (ABC5). `AuthCard` is ABC5-07's one small card on
 * the canvas: forgot, reset, invite.
 *
 * `data-frame` marks what `scripts/frame-check.mjs` measures.
 */

/** The FM mark is decoration: the link's name is "Form-Magic". */
function Brand() {
  return <Link to="/" className="auth-brand"><span className="mark" aria-hidden="true">FM</span>Form-Magic</Link>;
}

export function AuthSplit({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="auth-page">
      <div className="auth-split">
        <main className="auth-main">
          <div className="auth-form" data-frame="auth-form">
            <Brand />
            {children}
          </div>
        </main>
        {aside && <aside className="auth-aside">{aside}</aside>}
      </div>
    </div>
  );
}

export function AuthCard({ children }: { children: ReactNode }) {
  return (
    <div className="auth-page auth-solo">
      <main className="auth-card" data-frame="auth-card">
        <Brand />
        {children}
      </main>
    </div>
  );
}

/**
 * A product screenshot for the aside — a Site content library entry (078;
 * the builder beside sign-in, the responses beside sign-up). Its phone crop,
 * WHOLE, as a card: sharp at this size and a tenth of the desktop capture's
 * weight. Until 2026-09-27 the crop filled a 260px strip and was cut to its
 * top, which on the builder was a form title and a blank — the page read as
 * having no screenshot at all. A shot with no phone crop shows its desktop
 * capture at the aside's full width instead.
 */
export function AuthShot({ shot: id }: { shot: string }) {
  const content = useSiteContent();
  if (!content) return null;
  const shot = shotOf(content, id);
  const img = shot.phone || shot.desktop;
  return (
    <div className={`auth-shot${shot.phone ? '' : ' wide'}`}>
      <img src={img.src} alt={shot.alt} width={img.width} height={img.height} loading="lazy" />
    </div>
  );
}

/**
 * "What's new": the newest release from Site content — the same list the
 * community's release history shows — so the sign-in page never goes stale
 * the way a hand-written line would. Nothing but the shot while it loads, or
 * if there is none.
 */
export function WhatsNew() {
  const [release, setRelease] = useState<Release | null>(null);
  useEffect(() => {
    let alive = true;
    fetchReleases()
      .then((r) => { if (alive && Array.isArray(r?.releases)) setRelease(r.releases[0] ?? null); })
      .catch(() => undefined);
    return () => { alive = false; };
  }, []);
  return (
    <>
      {release && (
        <>
          <span className="auth-kicker">What’s new in v{release.version}</span>
          <h2 className="auth-aside-title">{release.title}</h2>
          {Array.isArray(release.highlights) && release.highlights.length > 0 && (
            <ul className="auth-ticks">
              {release.highlights.slice(0, 3).map((h, i) => <li key={i}>{h}</li>)}
            </ul>
          )}
          <Link to="/community/releases">Every release →</Link>
        </>
      )}
      <AuthShot shot="builder" />
    </>
  );
}

/** Two letters for an account's tile: "Acme Field Services" → "AF", "bctelo" → "BC". */
export function initialsOf(name: string | null | undefined): string {
  const words = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '·';
  const two = words.length > 1 ? words[0][0] + words[1][0] : words[0].slice(0, 2);
  return two.toUpperCase();
}
